
import { Options, Vue } from 'vue-class-component'
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import IconRfid from '@/components/svg/IconRfid.vue'
import IconArrowBack from '@/components/svg/IconArrowBack.vue'
import RfidItemSelector from '@/views/bizBrowser/RfidItemSelector.vue'
import { FETCH_BARCODE, SAVE_SCANNED_BARCODES, UNPAIRING } from '@/store/actions'
import { unpairBarcode, getBarcode } from '@/utils/api'
import { IBarcode, IBarcodeDefinitionType, TError } from 'smartbarcode-web-core/src/utils/types'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { RfidScanParsedResult } from '@/utils/bizBrowser/rfidScanResultHandler'
import errorHandler from '@/utils/errorHandler'
import { openMessage } from '@/utils/helpers'

@Options({
  components: {
    BarcodeLabelList,
    IconRfid,
    IconArrowBack,
    RfidItemSelector,
  },
  name: 'BarcodeUnpairScanRfid',
})
export default class BarcodeUnpairScanRfid extends Vue {
  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  scannedBarcodeList: IBarcode[] = []
  showRfidScanDialog = true

  async created() {
    this.loading = true
    await this.getBarcodeInfo()
    this.loading = false
  }

  goBack() {
    this.$router.back()
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (to.name === 'scanned-barcode-list') {
      next()
      return
    }

    if (this.scannedBarcodeList.length > 0) {
      try {
        await this.$confirm(this.$t('confirm_rfid_scan_result_is_not_saved_when_leaving_page'), this.$t('warning'), {
          confirmButtonText: this.$t('ok'),
          cancelButtonText: this.$t('cancel'),
          type: 'warning',
        })
        next()
      } catch {
        next(false)
      }
    } else {
      next()
    }
  }

  async getBarcodeInfo() {
    if (this.$route.params?.barcodeId && this.$route.params?.project) {
      await this.$store.dispatch(FETCH_BARCODE, { id: this.$route.params?.barcodeId })
    }
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  barcodeTypeInfo(barcodeType: string): Record<string, IBarcodeDefinitionType> {
    return this.$store.state.project?.details?.barcodeTypes[barcodeType]
  }

  async validateBarcode(resultScanned: IBarcode) {
    if (this.barcode.id === resultScanned.id) throw this.$t('barcode_unpairing_by_itself_error')

    const currentProjectId = this.$store.state.project.details.id
    const scannedBCProjectId = resultScanned.projectId
    if (currentProjectId !== scannedBCProjectId) throw this.$t('scanned_barcode_not_same_project')

    if (!resultScanned.isActivated) throw this.$t('barcode not activated')

    await unpairBarcode({
      parentBarcodeId: this.barcode.id,
      isUnpairAll: false,
      unpairChildrenIds: [resultScanned?.id],
      isDryRun: true,
    })
  }

  async onRfidScanConfirmed(rfidList: RfidScanParsedResult[]) {
    this.loading = true
    try {
      const projectCode = this.$route?.params?.project?.toString().split('@')[0] || ''

      for (const rfidResult of rfidList) {
        try {
          const barcodeId = `projectCode=${projectCode}`
          const scannedBarcode = await getBarcode(barcodeId, rfidResult.parsedValue)

          const isAlreadyAdded = this.scannedBarcodeList.find((barcode: IBarcode) => barcode.id === scannedBarcode.id)
          if (isAlreadyAdded) {
            continue
          }

          await this.validateBarcode(scannedBarcode)
          this.scannedBarcodeList.push(scannedBarcode)
          openMessage(this.$t('barcode detected'), 'success')
        } catch (error) {
          if (error !== 'cancel') {
            errorHandler(error as TError)
          }
        }
      }
    } finally {
      this.loading = false
    }
  }

  async submit() {
    if (!this.scannedBarcodeList || this.scannedBarcodeList.length === 0) {
      return
    }
    this.$store.dispatch(SAVE_SCANNED_BARCODES, this.scannedBarcodeList)
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('unpairing_read_result'),
        operation: UNPAIRING,
      },
    })
  }
}
